import React, { useState, useCallback, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from "react-router-dom";
import axios from "axios";
import Login from "./Login";
import Dashboard from "./Dashboard";
import Orders from "./Orders";
import Returns from "./Returns";
import Marketplaces from "./Marketplaces";
import Invoices from "./Invoices";
import Topbar from "./Topbar";
import Settings from "./Settings";
import FileManager from "./FileManager";
import { LanguageContext } from "./LanguageContext";

// Add axios interceptor for handling 401 errors globally
let isHandling401 = false;

const setupAxiosInterceptors = (handleLogout) => {
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401 && !isHandling401) {
        isHandling401 = true;
        console.log("Token expired, logging out...");
        handleLogout();
        setTimeout(() => {
          isHandling401 = false;
        }, 1000);
      }
      return Promise.reject(error);
    }
  );
};

const resolveApiBase = () => {
  const env = process.env.REACT_APP_API_URL;
  if (env && env.trim().length > 0) {
    const trimmed = env.trim().replace(/\/+$/, "");
    return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  }
  if (typeof window !== "undefined" && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")) {
    return "http://127.0.0.1:8000";
  }
  return "/api";
};
const API_URL = resolveApiBase();

const APP_TITLE = "Nestos Shop Panel";

const SUPPORTED_LANGUAGES = ["en", "tr"];
const normalizeLanguage = (value) => (SUPPORTED_LANGUAGES.includes(value) ? value : "en");

const AppLayout = ({
  handleLogout,
  marketplaces,
  selectedMarketplace,
  setSelectedMarketplace,
  username,
  role,
  language,
  onLanguageChange
}) => (
  <div className="min-h-screen bg-background text-foreground">
    <Topbar 
      handleLogout={handleLogout}
      marketplaces={marketplaces}
      selectedMarketplace={selectedMarketplace}
      setSelectedMarketplace={setSelectedMarketplace}
      username={username}
      role={role}
      language={language}
      onLanguageChange={onLanguageChange}
    />
    <main className="mx-auto w-full max-w-7xl px-4 pb-16 pt-24 sm:px-6 lg:px-8 lg:pt-28">
      <Outlet /> 
    </main>
  </div>
);

function App() {
  const [token, setToken] = useState(() => localStorage.getItem("token"));
  const [marketplaces, setMarketplaces] = useState([]);
  const [selectedMarketplace, setSelectedMarketplace] = useState(() => localStorage.getItem("selectedMarketplace") || "");
  const [username, setUsername] = useState(() => localStorage.getItem("username") || "");
  const [role, setRole] = useState(() => localStorage.getItem("role") || "");
  const [language, setLanguage] = useState(() => normalizeLanguage(localStorage.getItem("language") || "en"));

  const handleSetToken = (newToken) => {
    localStorage.setItem("token", newToken);
    setToken(newToken);
  };

  const handleLogout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("selectedMarketplace");
    localStorage.removeItem("username");
    localStorage.removeItem("role");
    setToken(null);
    setMarketplaces([]);
    setSelectedMarketplace("");
    setUsername("");
    setRole("");
  }, []);

  // Setup axios interceptors when component mounts
  useEffect(() => {
    setupAxiosInterceptors(handleLogout);
  }, [handleLogout]);

  const handleSetLanguage = useCallback(
    (value) => {
      const nextLanguage = normalizeLanguage(value);
      setLanguage(nextLanguage);
      localStorage.setItem("language", nextLanguage);
    },
    []
  );

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = language;
    }
  }, [language]);

  const handleSetSelectedMarketplace = (marketplaceCode) => {
    localStorage.setItem("selectedMarketplace", marketplaceCode);
    setSelectedMarketplace(marketplaceCode);
  };

  const fetchMarketplaces = useCallback(async () => {
    if (token) {
        try {
            const response = await axios.get(`${API_URL}/marketplaces/`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const fetchedMarketplaces = response.data;
            setMarketplaces(fetchedMarketplaces);

            if (fetchedMarketplaces.length > 0) {
          const storedMarketplace = localStorage.getItem("selectedMarketplace");
          if (fetchedMarketplaces.some((m) => m.country_code === storedMarketplace)) {
                    setSelectedMarketplace(storedMarketplace);
                } else {
                    handleSetSelectedMarketplace(fetchedMarketplaces[0].country_code);
                }
            }
        } catch (err) {
            console.error("Failed to fetch marketplaces", err);
            if (err.response && err.response.status === 401) {
                handleLogout();
            }
        }
    }
  }, [token, handleLogout]);

  useEffect(() => {
    fetchMarketplaces();
  }, [fetchMarketplaces]);

  useEffect(() => {
    if (!token) {
      return;
    }
    const fetchProfile = async () => {
      try {
        const response = await axios.get(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const { username: apiUsername, role: apiRole } = response.data;
        if (apiUsername) {
          setUsername(apiUsername);
          localStorage.setItem("username", apiUsername);
        }
        if (apiRole) {
          setRole(apiRole);
          localStorage.setItem("role", apiRole);
        }
      } catch (error) {
        console.error("Failed to fetch profile", error);
      }
    };
    fetchProfile();
  }, [token]);

  useEffect(() => {
    document.title = APP_TITLE;
  }, []);

  return (
    <LanguageContext.Provider value={{ language, setLanguage: handleSetLanguage }}>
    <Router>
      <Routes>
        <Route path="/login" element={!token ? <Login setToken={handleSetToken} setUsername={setUsername} /> : <Navigate to="/" />} />
        
        <Route 
          element={
            token ? (
              <AppLayout 
                handleLogout={handleLogout}
                marketplaces={marketplaces}
                selectedMarketplace={selectedMarketplace}
                setSelectedMarketplace={handleSetSelectedMarketplace}
                username={username}
                role={role}
                  language={language}
                  onLanguageChange={handleSetLanguage}
              />
            ) : (
              <Navigate to="/login" />
            )
          }
        >
            <Route path="/" element={<Dashboard />} />
            <Route path="/orders" element={<Orders token={token} selectedMarketplace={selectedMarketplace} />} />
            <Route path="/invoices" element={<Invoices token={token} role={role} />} />
            <Route path="/returns" element={<Returns token={token} />} />
            <Route path="/marketplaces" element={<Marketplaces token={token} onMarketplaceAdd={fetchMarketplaces} />} />
            <Route path="/files" element={<FileManager token={token} />} />
            <Route path="/settings" element={<Settings token={token} role={role} />} />
        </Route>
      </Routes>
    </Router>
    </LanguageContext.Provider>
  );
}

export default App;
